// Phase 5A / Iter94 — Shared Cockpit Safety Strip.
//
// A single read-only chip strip mounted at the very top of the
// Dashboard summarising whether the operator has the in-memory
// context needed to run a controlled module auto-scan. Pulls
// exclusively from three existing in-memory React providers:
//
//   * MarketSnapshotContext
//   * WESContextProvider
//   * CFMInventoryContext
//
// HARD CONSTRAINTS (asserted by Iter94 tests):
//  * Pure presentation. No axios, no fetch primitive, no
//    XMLHttpRequest, no Beacon API.
//  * No effect hooks. No mount-time HTTP. No scheduler. The strip
//    is a pure render of the three already-published contexts.
//  * Never blocks scans. Never ranks. Never recommends a module.
//    Never picks a winner. The overall readiness chip is
//    informational only — the existing per-panel evaluators decide
//    whether a scan returns READY / NO_TRADE / BLOCKED.
//  * No persistence layer (no localStorage / sessionStorage /
//    IndexedDB / document.cookie).
//  * No new endpoint. No new backend route. No new feature.

import React from "react";
import {
  ShieldCheck, AlertTriangle, Activity, Boxes, Layers,
} from "lucide-react";
import { useMarketSnapshot } from "@/contexts/MarketSnapshotContext";
import { useWESContext } from "@/contexts/WESContextProvider";
import {
  useCFMInventory, isSharesReady, isLeapsReady,
} from "@/contexts/CFMInventoryContext";
import {
  summarizeWesContext,
} from "@/components/wesContextPayloadHelpers";
import RefreshBothControl from "@/components/RefreshBothControl";

const SAFETY_LABELS = Object.freeze([
  { testid: "cockpit-strip-label-readiness-only",
    text: "Cockpit readiness only." },
  { testid: "cockpit-strip-label-no-scan",
    text: "No scan triggered." },
  { testid: "cockpit-strip-label-no-ranking",
    text: "No ranking." },
  { testid: "cockpit-strip-label-no-execution",
    text: "No execution." },
  { testid: "cockpit-strip-label-human-decides",
    text: "Human decides." },
]);

const STALE_SECONDS = 5 * 60;

function ageSecondsOf(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}

function fmtAge(secs) {
  if (secs == null) return "—";
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}

function classifySnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") {
    return { kind: "missing", age: null,
             partial: false, rate_limited: false };
  }
  const age = ageSecondsOf(snapshot.as_of);
  const stale = age != null && age > STALE_SECONDS;
  return {
    kind: stale ? "stale" : "fresh",
    age,
    partial: !!snapshot.partial,
    rate_limited: !!snapshot.rate_limited,
  };
}

function classifyWes(wesContext) {
  const sum = summarizeWesContext(wesContext);
  if (!sum.available) {
    return {
      kind: "missing", age: null, fingerprintShort: null,
      partial: false, unconfirmed: false, errors: false,
      symbolsCount: 0,
    };
  }
  // Unconfirmed bar / errors flags surfaced from any row.
  let unconfirmed = false;
  let errors = false;
  const rows = Array.isArray(wesContext?.rows) ? wesContext.rows : [];
  for (const r of rows) {
    if (r && r.bar_closed === false) unconfirmed = true;
    if (r && Array.isArray(r.errors) && r.errors.length > 0) errors = true;
  }
  return {
    kind: sum.stale ? "stale" : "fresh",
    age: sum.ageSeconds,
    fingerprintShort: sum.fingerprintShort,
    partial: !!wesContext?.partial,
    unconfirmed,
    errors,
    symbolsCount: sum.availableSymbols.length,
  };
}

function classifyInventory(invRows) {
  const rows = Array.isArray(invRows) ? invRows : [];
  let sharesReady = 0;
  let leapsReady = 0;
  for (const r of rows) {
    if (isSharesReady(r)) sharesReady += 1;
    if (isLeapsReady(r))  leapsReady  += 1;
  }
  const anyReady = sharesReady > 0 || leapsReady > 0;
  return {
    kind: anyReady ? "ready" : "missing",
    sharesReady,
    leapsReady,
    total: rows.length,
  };
}

function classifyOverall(snap, wes, inv) {
  // Order of precedence:
  //   1. MISSING CONTEXT  — no snapshot AND no WES (cold start).
  //   2. RATE LIMITED     — explicit rate-limited flag from either.
  //   3. STALE            — snapshot or WES > 5 minutes old.
  //   4. PARTIAL          — partial flag from either, or unconfirmed
  //                         WES bar, or WES errors.
  //   5. READY            — snapshot fresh + WES fresh.
  // Informational only. Never blocking.
  if (snap.kind === "missing" && wes.kind === "missing") {
    return "MISSING CONTEXT";
  }
  if (snap.rate_limited || (wes.partial && wes.errors)) {
    return "RATE LIMITED";
  }
  if (snap.kind === "stale" || wes.kind === "stale") {
    return "STALE";
  }
  if (snap.kind === "missing" || wes.kind === "missing"
      || snap.partial || wes.partial
      || wes.unconfirmed || wes.errors) {
    return "PARTIAL";
  }
  return "READY";
}

function overallColor(kind) {
  switch (kind) {
    case "READY":           return "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]";
    case "PARTIAL":         return "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]";
    case "STALE":           return "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]";
    case "RATE LIMITED":    return "border-[#FF8800]/40 bg-[#FF8800]/10 text-[#FF8800]";
    case "MISSING CONTEXT":
    default:                return "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]";
  }
}

function Chip({ testid, kind, label, value, sub }) {
  const colorByKind = {
    fresh:        "border-[#00FF88]/30 bg-[#00FF88]/5 text-[#00FF88]",
    ready:        "border-[#00FF88]/30 bg-[#00FF88]/5 text-[#00FF88]",
    stale:        "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]",
    partial:      "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]",
    rate_limited: "border-[#FF8800]/40 bg-[#FF8800]/5 text-[#FF8800]",
    unconfirmed:  "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]",
    errors:       "border-[#FF3366]/40 bg-[#FF3366]/5 text-[#FF3366]",
    missing:      "border-[#FF3366]/40 bg-[#FF3366]/5 text-[#FF3366]",
    neutral:      "border-[#26262B] text-[#8A8A93]",
  };
  return (
    <span
      data-testid={testid}
      className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] inline-flex items-center gap-1 ${colorByKind[kind] || colorByKind.neutral}`}
    >
      <span className="font-bold">{label}</span>
      {value != null && <span>{value}</span>}
      {sub && <span className="opacity-70">· {sub}</span>}
    </span>
  );
}

export default function SharedCockpitSafetyStrip() {
  const { snapshot } = useMarketSnapshot();
  const { wesContext } = useWESContext();
  const { rows: invRows } = useCFMInventory();

  const snap = classifySnapshot(snapshot);
  const wes  = classifyWes(wesContext);
  const inv  = classifyInventory(invRows);
  const overall = classifyOverall(snap, wes, inv);

  return (
    <div
      data-testid="cockpit-strip"
      className="mb-3 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      <div className="px-4 py-2 flex items-center gap-x-3 gap-y-1 flex-wrap text-[10px] font-mono">
        <ShieldCheck size={14} className="text-[#E0E0E0]" />
        <span
          data-testid="cockpit-strip-title"
          className="font-bold uppercase tracking-[0.18em] text-[#E0E0E0]"
        >
          Cockpit Status
        </span>
        <span
          data-testid="cockpit-strip-overall"
          className={`px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-[0.14em] font-bold ${overallColor(overall)}`}
        >
          {overall}
        </span>

        <span className="text-[#5F5F66]">|</span>

        <SnapshotGroup snap={snap} />

        <span className="text-[#5F5F66]">|</span>

        <WesGroup wes={wes} />

        <span className="text-[#5F5F66]">|</span>

        <InventoryGroup inv={inv} />
      </div>

      <div
        data-testid="cockpit-strip-banner"
        className="px-4 py-1.5 border-t border-[#26262B] bg-[#08080A] flex items-center gap-3 flex-wrap text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66]"
      >
        {SAFETY_LABELS.map(l => (
          <span key={l.testid} data-testid={l.testid}>
            {l.text}
          </span>
        ))}
      </div>

      {/* Iter95 — One-Click Refresh Both action. Manual-only, no
          new endpoint, no persistence, no scanner run. */}
      <RefreshBothControl />
    </div>
  );
}

function SnapshotGroup({ snap }) {
  const { kind, age, partial, rate_limited } = snap;
  return (
    <span
      data-testid="cockpit-strip-snapshot-group"
      className="inline-flex items-center gap-1"
    >
      <Activity size={11} className="text-[#5F5F66]" />
      <span className="text-[#5F5F66] uppercase tracking-[0.14em]">SNAP</span>
      {kind === "missing" && (
        <Chip testid="cockpit-strip-snapshot-missing"
              kind="missing" label="MISSING" />
      )}
      {kind === "fresh" && (
        <Chip testid="cockpit-strip-snapshot-fresh"
              kind="fresh" label="FRESH" value={fmtAge(age)} />
      )}
      {kind === "stale" && (
        <Chip testid="cockpit-strip-snapshot-stale"
              kind="stale" label="STALE" value={fmtAge(age)} />
      )}
      {partial && (
        <Chip testid="cockpit-strip-snapshot-partial"
              kind="partial" label="PARTIAL" />
      )}
      {rate_limited && (
        <Chip testid="cockpit-strip-snapshot-rate-limited"
              kind="rate_limited" label="RATE-LIMITED" />
      )}
    </span>
  );
}

function WesGroup({ wes }) {
  const {
    kind, age, fingerprintShort, partial, unconfirmed,
    errors, symbolsCount,
  } = wes;
  return (
    <span
      data-testid="cockpit-strip-wes-group"
      className="inline-flex items-center gap-1"
    >
      <Layers size={11} className="text-[#5F5F66]" />
      <span className="text-[#5F5F66] uppercase tracking-[0.14em]">WES</span>
      {kind === "missing" && (
        <Chip testid="cockpit-strip-wes-missing"
              kind="missing" label="MISSING" />
      )}
      {kind === "fresh" && (
        <Chip testid="cockpit-strip-wes-fresh"
              kind="fresh" label="FRESH"
              value={fmtAge(age)} sub={`${symbolsCount} sym`} />
      )}
      {kind === "stale" && (
        <Chip testid="cockpit-strip-wes-stale"
              kind="stale" label="STALE" value={fmtAge(age)} />
      )}
      {partial && (
        <Chip testid="cockpit-strip-wes-partial"
              kind="partial" label="PARTIAL" />
      )}
      {unconfirmed && (
        <Chip testid="cockpit-strip-wes-unconfirmed"
              kind="unconfirmed" label="BAR UNCONFIRMED" />
      )}
      {errors && (
        <Chip testid="cockpit-strip-wes-errors"
              kind="errors" label="ERRORS" />
      )}
      {fingerprintShort && (
        <Chip testid="cockpit-strip-wes-fingerprint"
              kind="fresh" label="FP" value={fingerprintShort} />
      )}
    </span>
  );
}

function InventoryGroup({ inv }) {
  return (
    <span
      data-testid="cockpit-strip-inventory-group"
      className="inline-flex items-center gap-1"
    >
      <Boxes size={11} className="text-[#5F5F66]" />
      <span className="text-[#5F5F66] uppercase tracking-[0.14em]">INV</span>
      <Chip testid="cockpit-strip-inventory-shares"
            kind={inv.sharesReady > 0 ? "ready" : "neutral"}
            label="SHARES" value={`${inv.sharesReady} ready`} />
      <Chip testid="cockpit-strip-inventory-leaps"
            kind={inv.leapsReady > 0 ? "ready" : "neutral"}
            label="LEAPS" value={`${inv.leapsReady} ready`} />
      {inv.kind === "missing" && (
        <Chip testid="cockpit-strip-inventory-missing"
              kind="missing" label="NONE READY" />
      )}
    </span>
  );
}
